<!--
  Copyright (C) 2023 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="codemirror-editor-root">
    <PrimeToolbar class="consultation-toolbar">
      <template v-slot:start>
        <h3 v-if="contentNode" class="consultation-title col">
          {{ contentNode.name }}
        </h3>
        <PrimeTag
          class="tag-version"
          v-if="$route.params.versionId"
          :value="$route.params.versionId"
        />
        <!-- Collaborative editing indicators -->
        <div
          v-if="isCollabActive"
          class="collab-indicators flex align-items-center gap-2 ml-3"
        >
          <span
            class="collab-status-dot"
            :class="{
              connected: collabIsConnected,
              reconnecting: collabIsReconnecting,
            }"
          />
          <span class="collab-status-text text-sm">
            {{
              collabIsConnected
                ? $t("collab.connected")
                : $t("collab.reconnecting")
            }}
          </span>
          <div
            v-if="collabPeerCount > 0"
            class="collab-peers flex align-items-center gap-1"
          >
            <span
              v-for="peer in connectedPeers.slice(0, 6)"
              :key="peer.clientId"
              class="collab-peer-avatar"
              :style="{ backgroundColor: peer.color }"
              v-tooltip.bottom="peer.displayName"
            >
              {{ peer.displayName.charAt(0).toUpperCase() }}
            </span>
            <span v-if="collabPeerCount > 6" class="collab-peer-more text-sm">
              +{{ collabPeerCount - 6 }}
            </span>
          </div>
          <!-- Collaborative editing participant counts -->
          <div
            v-if="isCollabActive && collabIsConnected"
            class="collab-participant-badges flex align-items-center gap-2"
          >
            <div
              class="collab-badge collab-badge-editor flex align-items-center gap-1"
              v-tooltip.bottom="$t('collab.editorsTooltip')"
            >
              <i class="ri-edit-fill" aria-hidden="true"></i>
              <span class="text-sm font-medium">{{
                collabEditorCount + (collabIsEditor ? 1 : 0)
              }}</span>
            </div>
            <div
              class="collab-badge collab-badge-observer flex align-items-center gap-1"
              v-tooltip.bottom="$t('collab.observersTooltip')"
            >
              <i class="ri-eye-fill" aria-hidden="true"></i>
              <span class="text-sm font-medium">{{
                collabObserverCount + (collabIsObserver ? 1 : 0)
              }}</span>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:end>
        <div class="flex gap-2 align-items-center">
          <!-- View Mode -->
          <PrimeSelectButton
            v-if="editing && (isMarkdown || isHtml)"
            v-model="viewMode"
            :options="viewModeOptions"
            option-value="value"
            :allow-empty="false"
            class="view-mode-selector"
          >
            <template #option="slotProps">
              <i
                :class="slotProps.option.icon"
                v-tooltip.bottom="slotProps.option.label"
                aria-hidden="true"
              />
            </template>
          </PrimeSelectButton>

          <!-- Editor -->
          <PrimeButton
            v-if="
              !readOnly &&
              !editing &&
              !collabIsSessionFull &&
              (!collabAvailable || collabPermissionLevel === 'WRITE')
            "
            icon="ri-pencil-line"
            :severity="collabUnavailableReason ? 'secondary' : 'info'"
            @click="
              collabUnavailableReason
                ? showCollabUnavailable()
                : toggleEditMode()
            "
            :disabled="isLocked || collabLoading"
            :loading="collabLoading"
            :aria-label="$t('editor.edit')"
            v-tooltip.bottom="
              collabUnavailableReason
                ? $t('collab.unavailable')
                : $t('editor.edit')
            "
            raised
          />
          <!-- Save -->
          <PrimeButton
            v-if="editing && !readOnly"
            icon="ri-save-line"
            severity="primary"
            @click="save"
            :disabled="isLocked || !hasChanges"
            :aria-label="$t('save')"
            v-tooltip.bottom="$t('save')"
            raised
          />
          <!-- Close editing -->
          <PrimeButton
            v-if="editing"
            icon="ri-logout-box-line"
            severity="warn"
            @click="closeEdit"
            v-tooltip.bottom="$t('editor.closeEdit')"
            raised
          />

          <div class="w-1rem">
            <i class="ri-more-2-line" aria-hidden="true"></i>
          </div>

          <!-- line Wrap -->
          <PrimeButton
            v-if="
              (editing && viewMode !== 'preview') ||
              (!editing && !isMarkdown && !isHtml)
            "
            :icon="'ri-text-wrap'"
            :severity="lineWrap ? 'info' : 'secondary'"
            @click="lineWrap = !lineWrap"
            :aria-label="$t('editor.lineWrap')"
            v-tooltip.bottom="$t('editor.lineWrap')"
            raised
          />

          <!-- Compare View -->
          <PrimeButton
            v-if="!isSharedLink"
            icon="ri-layout-column-line"
            severity="secondary"
            @click="openCompareDialog"
            :disabled="!canCompare"
            :aria-label="$t('diffViewer.compareTitle')"
            v-tooltip.bottom="$t('diffViewer.compareTitle')"
            raised
          />

          <!-- Download -->
          <PrimeButton
            icon="ri-download-line"
            severity="secondary"
            @click="download"
            :aria-label="$t('download')"
            v-tooltip.bottom="$t('download')"
            raised
          />
        </div>
      </template>
    </PrimeToolbar>

    <PrimeMessage
      v-if="isLocked && !readOnly"
      severity="warn"
      icon="ri-lock-line"
      class="p-small-message p-0"
    >
      {{ lockInfo }}
    </PrimeMessage>

    <!-- Collaborative mode session full warning -->
    <PrimeMessage
      v-if="collabIsSessionFull"
      severity="warn"
      icon="ri-group-line"
      class="p-small-message px-3 py-0"
    >
      {{ $t("collab.sessionFull") }} ({{ collabMaxPeers }}/{{ collabMaxPeers }})
    </PrimeMessage>

    <!-- Collaborative mode: Alfresco session/ticket expired -->
    <PrimeMessage
      v-if="collabIsAuthExpired"
      severity="error"
      icon="ri-lock-unlock-line"
      class="p-small-message px-3 py-0"
      :closable="false"
    >
      {{ $t("collab.authExpired") }}
      <div class="flex gap-2 ml-3">
        <PrimeButton
          :label="$t('collab.reload')"
          icon="ri-refresh-line"
          severity="danger"
          size="small"
          outlined
          :aria-label="$t('collab.reload')"
          @click="reloadPage"
        />
      </div>
    </PrimeMessage>

    <!-- Collaborative mode offline warning -->
    <PrimeMessage
      v-if="isCollabActive && collabIsReconnecting"
      severity="warn"
      icon="ri-wifi-off-line"
      class="p-small-message p-0"
    >
      {{ $t("collab.offline") }}
    </PrimeMessage>

    <div
      class="card editor-wrapper border-1 surface-border border-round overflow-hidden flex flex-column p-0 m-0"
    >
      <!-- Case 1: EDIT Mode -->
      <div
        v-if="editing"
        class="edit-mode-wrapper flex-grow-1 flex flex-column min-h-0"
      >
        <!-- Split view: editor + preview -->
        <PrimeSplitter
          v-if="(isMarkdown || isHtml) && viewMode === 'split'"
          class="flex-grow-1 h-full"
        >
          <PrimeSplitterPanel class="flex" :size="50" :min-size="10">
            <div ref="editorContainer" class="w-full flex-grow-1"></div>
          </PrimeSplitterPanel>
          <PrimeSplitterPanel class="flex" :size="50" :min-size="10">
            <iframe
              ref="splitPreviewIframe"
              class="preview-container w-full flex-grow-1"
              sandbox="allow-scripts allow-popups"
            ></iframe>
          </PrimeSplitterPanel>
        </PrimeSplitter>
        <!-- Editor only -->
        <div
          v-else-if="(!isMarkdown && !isHtml) || viewMode === 'editor'"
          ref="editorContainer"
          class="editor-only-container w-full flex-grow-1"
        ></div>
        <!-- Preview only -->
        <iframe
          v-else-if="viewMode === 'preview'"
          ref="editPreviewIframe"
          class="preview-iframe w-full flex-grow-1"
          sandbox="allow-scripts allow-popups"
        ></iframe>
      </div>

      <!-- Case 2: CONSULTATION Mode -->
      <div v-else class="consultation-view flex-grow-1">
        <iframe
          v-if="isMarkdown || isHtml"
          ref="consultPreviewIframe"
          class="preview-iframe w-full flex-grow-1"
          sandbox="allow-scripts allow-popups"
        ></iframe>
        <div
          v-else
          ref="consultationEditorContainer"
          class="consultation-editor-container"
        ></div>
      </div>
    </div>

    <DiffViewerDialog
      v-if="contentNode"
      v-model:visible="isCompareDialogVisible"
      :content-node="contentNode"
      :versions="versions"
      :initial-version-id="$route.params.versionId"
      @revert-version="handleRevertVersion"
    />
  </div>
</template>

<script setup>
import {
  ref,
  watch,
  computed,
  nextTick,
  onBeforeUnmount,
  onUnmounted,
  shallowRef,
} from "vue";
import { useI18n } from "vue-i18n";

// --- PrimeVue Imports ---
import PrimeButton from "primevue/button";
import PrimeToolbar from "primevue/toolbar";
import PrimeTag from "primevue/tag";
import PrimeMessage from "primevue/message";
import PrimeSplitter from "primevue/splitter";
import PrimeSplitterPanel from "primevue/splitterpanel";
import PrimeSelectButton from "primevue/selectbutton";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import DiffViewerDialog from "@/components/DiffViewerDialog.vue";

// --- CodeMirror Imports ---
import {
  getLanguageExtension,
  getBaseExtensions,
  EditorState,
  EditorView,
} from "@/composables/useCodeMirror.js";

// --- Markdown Preview ---
import { useMarkdownPreview } from "@/composables/useMarkdownPreview.js";

// --- Collaborative Editing Imports ---
import {
  useCollabEditor,
  ROLE_EDITOR,
  ROLE_OBSERVER,
} from "@/composables/useCollabEditor.js";
import { useCollabStore } from "@/stores/collab.js";

// --- Alfresco Imports ---
import { useUserStore } from "@/stores/user.js";
import { useTheme } from "@/composables/useTheme.js";
import { useScreenStore } from "@/stores/screen.js";
import { nodesApi } from "@/AlfrescoApi.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";

// --- i18n ---
const { t, te } = useI18n();

// --- PrimeVue Services ---
const toast = useToast();
const confirm = useConfirm();

// --- Theme ---
const { isDarkMode } = useTheme();

// --- Screen ---
const screenStore = useScreenStore();

// --- Collaborative Editing ---
const collabStore = useCollabStore();
const {
  isCollabActive,
  isSessionFull: collabIsSessionFull,
  isAuthExpired: collabIsAuthExpired,
  connectedPeers,
  isConnected: collabIsConnected,
  isReconnecting: collabIsReconnecting,
  peerCount: collabPeerCount,
  myRole,
  isEditor: collabIsEditor,
  isObserver: collabIsObserver,
  editorCount: collabEditorCount,
  observerCount: collabObserverCount,
  startVersion,
  startCollab,
  stopCollab,
  setEditorView,
  createCollabExtensions,
  createUpdateListener,
  requestSave,
  promoteToEditor,
} = useCollabEditor();

// Reload the page so the user re-authenticates after their Alfresco ticket expired.
const reloadPage = () => window.location.reload();

// --- Props ---
const props = defineProps({
  contentUrl: { type: String, default: null },
  contentNode: { type: Object, default: null },
  readOnly: { type: Boolean, default: false },
  isSharedLink: { type: Boolean, default: false },
});

// --- Reactive State ---
const editorContent = ref("");
const initialContent = ref("");
const lockInfo = ref(null);
const isLocked = ref(false);
const editing = ref(false);
const viewMode = ref(screenStore.isSmallScreen ? "editor" : "split");
const lineWrap = ref(false);
const currentMimeType = ref(null);
const editorContainer = ref(null);
const consultationEditorContainer = ref(null);
const splitPreviewIframe = ref(null);
const editPreviewIframe = ref(null);
const consultPreviewIframe = ref(null);
const editView = shallowRef(null);
const consultView = shallowRef(null);
const versions = ref([]);
const isCompareDialogVisible = ref(false);

// --- Collaborative Editing State ---
const collabAvailable = ref(false);
const collabUnavailableReason = ref(null);
const collabUnavailableDetails = ref(null);
const collabLoading = ref(false);
const collabMaxPeers = ref(0);
const collabPermissionLevel = ref(null);

// --- Debounced preview content ---
const PREVIEW_DEBOUNCE_MS = 300;
const previewContent = ref("");
let previewDebounceTimer = null;

watch(
  editorContent,
  (newContent) => {
    clearTimeout(previewDebounceTimer);
    previewDebounceTimer = setTimeout(() => {
      previewContent.value = newContent;
    }, PREVIEW_DEBOUNCE_MS);
  },
  { immediate: true },
);

onUnmounted(() => {
  clearTimeout(previewDebounceTimer);
});

// --- Markdown Preview ---
const { isMarkdown, isHtml, iframeContent, previewBodyHtml } =
  useMarkdownPreview({
    content: previewContent,
    mimeType: currentMimeType,
  });

// --- Computed Properties ---
const hasChanges = computed(() => editorContent.value !== initialContent.value);
const canCompare = computed(() => versions.value && versions.value.length >= 2);
const viewModeOptions = computed(() => {
  const options = [
    {
      value: "editor",
      icon: "ri-code-line",
      label: t("editor.viewMode.editorOnly"),
    },
  ];
  if (!screenStore.isSmallScreen) {
    options.push({
      value: "split",
      icon: "ri-layout-column-fill",
      label: t("editor.viewMode.split"),
    });
  }
  options.push({
    value: "preview",
    icon: "ri-eye-line",
    label: t("editor.viewMode.previewOnly"),
  });
  return options;
});

// --- Logic ---
const createEditor = (isEditable) => {
  const targetRef = isEditable ? editorContainer : consultationEditorContainer;
  if (!targetRef.value) {
    return;
  }

  const langExtension = getLanguageExtension(currentMimeType.value);
  const baseExtensions = getBaseExtensions({
    editable: isEditable,
    showLineNumbers: true,
    enableHistory: isEditable,
    dark: isDarkMode(),
    lineWrapping: lineWrap.value,
    onUpdate: isEditable
      ? (content) => {
          editorContent.value = content;
        }
      : null,
  });

  const extensions = [...baseExtensions];
  if (
    langExtension &&
    (Array.isArray(langExtension) ? langExtension.length > 0 : true)
  ) {
    extensions.push(langExtension);
  }

  const state = EditorState.create({ doc: editorContent.value, extensions });
  const view = new EditorView({ state, parent: targetRef.value });

  if (isEditable) {
    editView.value = view;
  } else {
    consultView.value = view;
  }
};

const destroyEditor = (viewRef) => {
  if (viewRef.value) {
    viewRef.value.destroy();
    viewRef.value = null;
  }
};

const loadContent = async (node) => {
  if (!node.id && !node.shareId) {
    return;
  }

  try {
    let blob;
    if (props.isSharedLink && node.shareId) {
      blob = await alfrescoNodeService.getSharedLinkContent(node.shareId);
    } else {
      blob = await alfrescoNodeService.getNodeContent(node.id, false);
    }

    let text = await blob.text();
    if (node.content?.mimeType === "application/json") {
      try {
        text = JSON.stringify(JSON.parse(text), null, 2);
      } catch (e) {
        // JSON is malformed, display as-is
        console.warn(
          "Failed to format JSON, displaying raw content:",
          e.message,
        );
      }
    }

    // Only check lock if not in read-only mode
    if (!props.readOnly) {
      await checkLock(node);
    }

    editorContent.value = text;
    initialContent.value = text;
    currentMimeType.value = node.content?.mimeType || "text/plain";

    // After content is loaded, create the consultation view if needed
    // Only create if it doesn't already exist (the watcher may have created it)
    await nextTick();
    if (
      !editing.value &&
      !isMarkdown.value &&
      !isHtml.value &&
      !consultView.value
    ) {
      createEditor(false);
    }
  } catch (err) {
    console.error("Failed to load content:", err);
    toast.add({
      severity: "error",
      summary: t("error.error"),
      detail: t("editor.loadError"),
      life: 3000,
    });
  }
};

const save = async () => {
  // In collab mode, request save via WebSocket
  if (isCollabActive.value) {
    try {
      const result = await requestSave();
      // Reset dirty state after successful save
      initialContent.value = editorContent.value;
      if (result.versionLabel) {
        toast.add({
          severity: "success",
          summary: t("success"),
          detail: `${t("editor.saveSuccess")} (v${result.versionLabel})`,
          life: 3000,
        });
      } else if (result.noChanges) {
        toast.add({
          severity: "info",
          summary: t("info"),
          detail: t("editor.alreadyUpToDate"),
          life: 3000,
        });
      } else {
        toast.add({
          severity: "success",
          summary: t("success"),
          detail: t("editor.saveSuccess"),
          life: 3000,
        });
      }
      // Reload version history to show the new version
      await fetchVersions(props.contentNode.id);
    } catch (err) {
      toast.add({
        severity: "error",
        summary: t("error.error"),
        detail: err.message,
        life: 4000,
      });
    }
    return;
  }

  // Non-collab mode: traditional save
  if (!hasChanges.value) {
    return;
  }
  try {
    await alfrescoNodeService.updateContentNode(
      props.contentNode.id,
      editorContent.value,
      currentMimeType.value,
    );
    initialContent.value = editorContent.value;
    toast.add({
      severity: "success",
      summary: t("success"),
      detail: t("editor.saveSuccess"),
      life: 3000,
    });
    // Reload version history to show the new version
    await fetchVersions(props.contentNode.id);
  } catch (err) {
    toast.add({
      severity: "error",
      summary: t("error.error"),
      detail: err.message,
      life: 4000,
    });
  }
};

const refresh = async () => {
  try {
    const refreshNode = await nodesApi.lockNode(props.contentNode.id, {
      timeToExpire: 0,
      type: "ALLOW_OWNER_CHANGES",
      lifetime: "EPHEMERAL",
    });
    await checkLock(refreshNode.entry);
    await loadContent(refreshNode.entry);
  } catch (err) {
    try {
      const refreshNode = await nodesApi.getNode(props.contentNode.id, {
        include: ["properties"],
      });
      await checkLock(refreshNode.entry);
    } catch (getNodeErr) {
      toast.add({
        severity: "error",
        summary: t("error.error"),
        detail: t("editor.refreshError"),
        life: 3000,
      });
    }
  }
};

const checkLock = async (node) => {
  const lockOwner = node.properties?.["cm:lockOwner"];
  const userStore = useUserStore();
  if (lockOwner && lockOwner.id !== userStore.person.id) {
    isLocked.value = true;
    lockInfo.value = t("node.lockedBy", { name: lockOwner.displayName });
  } else {
    isLocked.value = false;
    lockInfo.value = null;
  }
};

const showCollabUnavailable = () => {
  const detailKey = `collab.unavailableDetail.${collabUnavailableDetails.value}`;
  const reasonKey = `collab.unavailableReason.${collabUnavailableReason.value}`;
  const detail = te(detailKey)
    ? t(detailKey)
    : te(reasonKey)
      ? t(reasonKey)
      : t("collab.unavailable");
  toast.add({
    severity: "warn",
    summary: t("collab.unavailable"),
    detail,
    life: 5000,
  });
};

const toggleEditMode = async () => {
  // Check if collab is enabled and we need to connect
  if (collabStore.enabled && collabAvailable.value) {
    collabLoading.value = true;
    try {
      // Start collab mode - no need to lock the file
      editing.value = true;

      // Wait for the editor to be created by the watcher
      await nextTick();
      await nextTick();

      // Additional safety: wait for editView to be set
      let attempts = 0;
      while (!editView.value && attempts < 10) {
        await nextTick();
        attempts++;
      }

      if (!editView.value) {
        throw new Error("Editor view not created");
      }

      // Now enable collab
      await enableCollab();

      if (isCollabActive.value) {
        toast.add({
          severity: "success",
          summary: t("success"),
          detail: t("collab.connected"),
          life: 3000,
        });
      } else {
        editing.value = false;
        toast.add({
          severity: "error",
          summary: t("error.error"),
          detail: t("collab.initError"),
          life: 4000,
        });
      }
    } catch (err) {
      console.error("Failed to start collaborative editing:", err);
      editing.value = false;
      toast.add({
        severity: "error",
        summary: t("error.error"),
        detail: t("collab.initError"),
        life: 4000,
      });
    } finally {
      collabLoading.value = false;
    }
  } else {
    // Non-collab mode: use traditional locking
    await refresh();
    if (!isLocked.value) {
      editing.value = true;
    }
  }
};

const closeCollab = async () => {
  // Save if there are unsaved changes
  if (hasChanges.value) {
    await save();
  }
  await disableCollab();
  // Reload content from server
  const blob = await alfrescoNodeService.getNodeContent(
    props.contentNode.id,
    false,
  );
  let text = await blob.text();
  if (props.contentNode.content?.mimeType === "application/json") {
    try {
      text = JSON.stringify(JSON.parse(text), null, 2);
    } catch (e) {
      // JSON is malformed, display as-is
    }
  }
  editorContent.value = text;
  initialContent.value = text;
  editing.value = false;

  // Reconnect as observer to continue receiving real-time updates
  await connectAsObserver(props.contentNode.id);
};

const closeEdit = async () => {
  if (isCollabActive.value) {
    await closeCollab();
    return;
  }
  // Non-collab: save if needed, then unlock and close
  if (hasChanges.value) {
    await save();
  }
  editing.value = false;
  await nodesApi.unlockNode(props.contentNode.id).catch(() => {});
};

const download = () => {
  const link = document.createElement("a");
  link.href = props.contentUrl;
  link.download = props.contentNode.name;
  link.click();
};

const fetchVersions = async (nodeId) => {
  if (!nodeId) {
    return;
  }
  try {
    const versionHistory =
      await alfrescoNodeService.listNodeVersionHistory(nodeId);
    versions.value = versionHistory.list.entries;
  } catch (err) {
    console.error("Error fetching versions:", err);
  }
};

const openCompareDialog = () => {
  isCompareDialogVisible.value = true;
};

const handleRevertVersion = async () => {
  // Recharger le contenu et les versions après la restauration
  await loadContent(props.contentNode);
  await fetchVersions(props.contentNode.id);
};

// --- Collaborative Editing Functions ---
const checkCollabAvailability = async (nodeId) => {
  if (!nodeId || props.readOnly) {
    collabAvailable.value = false;
    return;
  }

  try {
    const result = await collabStore.checkAvailability(nodeId);
    // Default to WRITE when backend does not return permissionLevel (backward compatibility)
    collabPermissionLevel.value = result.permissionLevel || "WRITE";
    collabMaxPeers.value = result.maxPeers || 0;

    collabAvailable.value =
      result.available === true && collabPermissionLevel.value !== "NONE";
    collabUnavailableReason.value =
      !collabAvailable.value && result.enabled ? result.reason || null : null;
    collabUnavailableDetails.value =
      !collabAvailable.value && result.enabled ? result.details || null : null;
  } catch (err) {
    console.error("[Collab] Failed to check collab availability:", err);
    collabAvailable.value = false;
    collabPermissionLevel.value = null;
  }
};

/**
 * Callback called when document is updated by the server (for observers)
 */
const handleDocumentUpdate = (document, version) => {
  // Update the content for observers (in read mode)
  if (!editing.value && document !== undefined) {
    editorContent.value = document;
    initialContent.value = document;

    // If we have a consultation view, update it
    if (consultView.value) {
      const transaction = consultView.value.state.update({
        changes: {
          from: 0,
          to: consultView.value.state.doc.length,
          insert: document,
        },
      });
      consultView.value.dispatch(transaction);
    }
  }
};

/**
 * Callback called when document is saved (by any peer in the session)
 */
const handleSaved = () => {
  // Reset dirty state - the document has been saved
  initialContent.value = editorContent.value;
};

/**
 * Connect to collab session as observer on page load
 */
const connectAsObserver = async (nodeId) => {
  if (
    !nodeId ||
    props.readOnly ||
    !collabStore.enabled ||
    !collabAvailable.value
  ) {
    return;
  }

  // Don't connect if already connected
  if (isCollabActive.value) {
    return;
  }

  try {
    const userStore = useUserStore();
    const displayName =
      userStore.person?.displayName || userStore.person?.id || "User";

    // Start collab as observer (no editor view yet, just WebSocket connection)
    await startCollab(
      nodeId,
      null,
      displayName,
      ROLE_OBSERVER,
      handleDocumentUpdate,
      handleSaved,
    );
  } catch (err) {
    console.error("[Collab] Failed to connect as observer:", err);
    // Non-blocking error - observer mode is optional
  }
};

const enableCollab = async () => {
  if (!props.contentNode?.id || !editView.value) {
    throw new Error("Missing node ID or editor view");
  }

  const userStore = useUserStore();
  const displayName =
    userStore.person?.displayName || userStore.person?.id || "User";

  let version;
  let document;

  // If already connected as observer, promote to editor
  if (isCollabActive.value && collabIsObserver.value) {
    const promoted = await promoteToEditor();
    version = promoted.version || startVersion.value;
    document = promoted.document;
  } else {
    // Start new collab session as editor
    const welcome = await startCollab(
      props.contentNode.id,
      null,
      displayName,
      ROLE_EDITOR,
      handleDocumentUpdate,
      handleSaved,
    );
    version = welcome.version;
    document = welcome.document;
  }

  // Update content from server (ensure we have the latest version)
  if (document !== undefined) {
    editorContent.value = document;
    initialContent.value = document;
  }

  // Recreate editor with collab extensions
  await recreateEditorWithCollab(version);
};

const disableCollab = async () => {
  try {
    await stopCollab();
  } catch (err) {
    console.error("Failed to stop collaborative editing:", err);
  }
};

const recreateEditorWithCollab = async (version) => {
  if (!editorContainer.value) {
    return;
  }

  // Destroy existing editor
  destroyEditor(editView);
  await nextTick();

  // Create new editor with collab extensions
  const langExtension = getLanguageExtension(currentMimeType.value);
  const baseExtensions = getBaseExtensions({
    editable: true,
    showLineNumbers: true,
    enableHistory: true,
    dark: isDarkMode(),
    lineWrapping: lineWrap.value,
    onUpdate: (content) => {
      editorContent.value = content;
    },
  });

  const collabExtensions = createCollabExtensions(version);

  // Add update listener for pushing changes to server
  const updateListener = EditorView.updateListener.of(createUpdateListener());

  const extensions = [...baseExtensions, ...collabExtensions, updateListener];
  if (
    langExtension &&
    (Array.isArray(langExtension) ? langExtension.length > 0 : true)
  ) {
    extensions.push(langExtension);
  }

  const state = EditorState.create({ doc: editorContent.value, extensions });
  editView.value = new EditorView({ state, parent: editorContainer.value });

  // Update the editor view reference in the collab composable
  setEditorView(editView.value);
};

// --- Preview iframe helpers ---
const getActivePreviewIframe = () => {
  if (editing.value) {
    if (viewMode.value === "split") {
      return splitPreviewIframe.value;
    }
    if (viewMode.value === "preview") {
      return editPreviewIframe.value;
    }
  } else {
    return consultPreviewIframe.value;
  }
  return null;
};

const initializePreviewIframe = (iframe) => {
  if (!iframe || iframe._previewInitialized) {
    return;
  }
  iframe.srcdoc = iframeContent.value;
  iframe._previewInitialized = true;
};

// Watch iframe refs to initialize them when they mount
watch([splitPreviewIframe, editPreviewIframe, consultPreviewIframe], () => {
  const iframe = getActivePreviewIframe();
  if (iframe) {
    initializePreviewIframe(iframe);
  }
});

// Send incremental updates via postMessage to preserve scroll position
watch(previewBodyHtml, (newHtml) => {
  const iframe = getActivePreviewIframe();
  if (!iframe || !iframe._previewInitialized) {
    return;
  }
  iframe.contentWindow?.postMessage(
    { type: "preview-update", html: newHtml },
    "*",
  );
});

// --- Watchers & Lifecycle Hooks ---

// Initialize collab store immediately (not in onMounted, because watchers run first)
collabStore.init();

watch(
  () => props.contentNode,
  async (newNode) => {
    if (newNode) {
      loadContent(newNode);
      fetchVersions(newNode.id);
      if (!props.readOnly) {
        // Wait for collab store to be initialized before checking availability
        if (!collabStore.loaded) {
          await collabStore.init();
        }
        await checkCollabAvailability(newNode.id);
        // Connect as observer to receive real-time updates
        await connectAsObserver(newNode.id);
      }
    }
  },
  { immediate: true },
);

watch(editing, async (isEditing) => {
  destroyEditor(editView);
  destroyEditor(consultView);
  await nextTick();
  if (isEditing) {
    if (viewMode.value !== "preview") {
      createEditor(true);
    }
  } else {
    if (!isMarkdown.value && !isHtml.value) {
      createEditor(false);
    }
  }
});

watch(viewMode, async (newMode, oldMode) => {
  if (!editing.value || !(isMarkdown.value || isHtml.value)) {
    return;
  }
  // Always destroy and recreate editor when view mode changes
  // because the editorContainer ref points to different DOM elements
  const needsEditor = newMode !== "preview";
  const hadEditor = oldMode !== "preview";
  if (hadEditor) {
    destroyEditor(editView);
  }
  if (needsEditor) {
    await nextTick();
    createEditor(true);
  }
});

watch(lineWrap, async () => {
  // Recreate the editor when line wrap setting changes
  if (editing.value && viewMode.value !== "preview" && editView.value) {
    destroyEditor(editView);
    await nextTick();
    createEditor(true);
  } else if (
    !editing.value &&
    !isMarkdown.value &&
    !isHtml.value &&
    consultView.value
  ) {
    destroyEditor(consultView);
    await nextTick();
    createEditor(false);
  }
});

onBeforeUnmount(async () => {
  // Stop collaborative editing if active
  if (isCollabActive.value) {
    await stopCollab();
  }

  destroyEditor(editView);
  destroyEditor(consultView);
  if (
    editing.value &&
    !isLocked.value &&
    !isCollabActive.value &&
    props.contentNode?.id
  ) {
    nodesApi.unlockNode(props.contentNode.id).catch(() => {});
  }
});
</script>

<style lang="scss" scoped>
.codemirror-editor-root {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
}

.consultation-toolbar {
  flex-wrap: wrap;
  max-width: 100%;

  :deep(.p-toolbar-start) {
    min-width: 0;
    flex: 1 1 0%;
    overflow: hidden;
  }

  :deep(.p-toolbar-end) {
    flex-shrink: 0;
  }

  .consultation-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 0 1 auto;
  }
}

.p-small-message {
  margin: 0;

  :deep(.p-message-wrapper) {
    padding: 0.5rem 1rem;
  }

  :deep(.p-message-text) {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}

.editor-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
}

.edit-mode-wrapper,
.consultation-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;

  :deep(.cm-editor) {
    flex: 1;
    min-height: 0;
    height: 100%;
  }

  :deep(.cm-scroller) {
    overflow: auto;
  }
}

.editor-only-container,
.consultation-editor-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  :deep(.cm-editor) {
    flex: 1;
    min-height: 0;
    height: 100%;
  }

  :deep(.cm-scroller) {
    overflow: auto;
    background: var(--surface-card);
  }
}

.code-editor-container {
  display: flex;
  flex-direction: column;
  overflow: auto;

  :deep(.cm-editor) {
    flex-grow: 1;
    height: 100%;
  }
}

.consultation-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: monospace;
  margin: 0;
  padding: 1rem;
  flex-grow: 1;
  overflow: auto;
}

.preview-iframe {
  flex: 1;
  min-height: 0;
  border: none;
}

.view-mode-selector {
  :deep(.p-button) {
    padding: 0.5rem 0.75rem;
  }
}

.split-view {
  flex-grow: 1;
}

:deep(.p-splitter) {
  flex-grow: 1;
  border: none;
}

:deep(.p-splitter-panel) {
  overflow: auto;
}

// Collaborative editing styles
.collab-peers,
.collab-indicators {
  border-left: 1px solid var(--p-surface-border);
  padding-left: 0.75rem;
}

.collab-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--p-gray-400);

  &.connected {
    background-color: var(--p-green-500);
  }

  &.reconnecting {
    background-color: var(--p-orange-500);
    animation: pulse 1.5s infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.collab-status-text {
  color: var(--p-text-muted-color);
}

.collab-peer-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.collab-peer-more {
  color: var(--p-text-muted-color);
}

.collab-participant-badges {
  gap: 0.5rem;
}

.collab-badge {
  padding: 0.25rem 0.5rem;
  border-radius: var(--p-border-radius);
  font-size: 0.875rem;
}

.collab-badge-editor {
  background-color: var(--p-green-100);
  color: var(--p-green-700);
  border: 1px solid var(--p-green-200);

  i {
    color: var(--p-green-500);
  }
}

.collab-badge-observer {
  background-color: var(--p-blue-100);
  color: var(--p-blue-700);
  border: 1px solid var(--p-blue-200);

  i {
    color: var(--p-blue-500);
  }
}

// Remote cursor styles (in editor)
:deep(.cm-remote-cursor) {
  position: relative;
  pointer-events: none;
}

:deep(.cm-remote-cursor-label) {
  pointer-events: none;
  z-index: 10;
}

:deep(.cm-remote-selection) {
  opacity: 0.3;
}
</style>
